import { IconSizes } from "atoms/constants/icon-sizes";
import { Icons } from "atoms/constants/icons";
import Icon from "atoms/icons/icon";
import { Resizable } from "re-resizable";
import React, { Children, useMemo } from "react";
import { PropsWithChildren } from "react";
import { Breakpoints } from "utilities/enumerations/breakpoints";
import useBreakpoint from "utilities/hooks/use-breakpoint";
import useResizeContainer from "utilities/hooks/use-resize-column";

const CSS_CLASS_NAME = "c-resizable-columns";
interface ResizeableColumnsProps extends PropsWithChildren {
    id: string;
    cssClassName?: string;
    onColumnResize?: () => void;
    startingWidth?: number;
    maxLeftWidth?: number;
    minLeftWidth?: number;
}

const ResizeableColumns = (props: ResizeableColumnsProps) => {
    const {
        children,
        cssClassName = "",
        id,
        startingWidth = 45,
        minLeftWidth = 20,
        maxLeftWidth = 70,
    } = props;
    const [leftColumn, rightColumn] = Children.toArray(children);
    const isDesktop = useBreakpoint(Breakpoints.Tablet);
    const { size, handleResizeStop } = useResizeContainer({
        id,
        startingResizableDimension: startingWidth,
    });

    // Creates wrapper to turn on/off based on screen size
    // Memoized to prevent refresh of children on resize
    const Wrapper = useMemo(
        () =>
            ({ children }) =>
                isDesktop ? (
                    <Resizable
                        enable={{ right: isDesktop }}
                        defaultSize={size}
                        onResizeStop={handleResizeStop}
                        handleComponent={{
                            right: (
                                <button
                                    className={`${CSS_CLASS_NAME}__dragButton`}>
                                    <div
                                        className={`${CSS_CLASS_NAME}__iconContainer`}>
                                        <Icon
                                            type={Icons.Resize}
                                            size={IconSizes.Medium}
                                        />
                                    </div>
                                </button>
                            ),
                        }}
                        maxWidth={`${maxLeftWidth}vw`}
                        minWidth={`${minLeftWidth}vw`}
                        handleStyles={{
                            right: { cursor: "grab", width: "1rem" },
                        }}
                        handleClasses={{ right: `${CSS_CLASS_NAME}__drag` }}>
                        {children}
                    </Resizable>
                ) : (
                    <>{children}</>
                ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isDesktop]
    );

    return (
        <div className={[CSS_CLASS_NAME, cssClassName].join(" ")}>
            <Wrapper>
                <div className={`${CSS_CLASS_NAME}__leftWrapper`}>
                    <div className={`${CSS_CLASS_NAME}__left`}>
                        {leftColumn}
                    </div>
                </div>
            </Wrapper>

            <div className={`${CSS_CLASS_NAME}__right`}>{rightColumn}</div>
        </div>
    );
};

export default ResizeableColumns;
